/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import * as Sentry from '@sentry/react';
import React from 'react';
import { ChakraProvider, CSSReset, defineStyle, defineStyleConfig, extendTheme } from '@chakra-ui/react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import App from './App';
import { store } from './app/Store';
import './index.css';
import DefaultTheme from './themes/default/Index';
import { initializeSentry } from './config/SentryConfiguration';

// Initialize Sentry

initializeSentry();

// Chakra theme
const buttonVariants = Object.fromEntries(
  Object.entries(DefaultTheme.buttonTheme).map(([name, props]) => [name, defineStyle(props)]),
);

const textVariants = Object.fromEntries(
  Object.entries(DefaultTheme.textTheme).map(([name, props]) => [name, defineStyle(props)]),
);

const theme = extendTheme({
  colors: DefaultTheme.colors,
  components: {
    Button: defineStyleConfig({ variants: buttonVariants }),
    Text: defineStyleConfig({ variants: textVariants }),
  },
});

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement, {
  // Callback called when an error is thrown and not caught by an ErrorBoundary.
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn('Uncaught error', error, errorInfo.componentStack);
  }),
  // Callback called when React catches an error in an ErrorBoundary.
  onCaughtError: Sentry.reactErrorHandler(),
  // Callback called when React automatically recovers from errors.
  onRecoverableError: Sentry.reactErrorHandler(),
});
root.render(
  <React.StrictMode>
    <Provider store={store}>
      <ChakraProvider theme={theme}>
        <CSSReset />
        <App />
      </ChakraProvider>
    </Provider>
  </React.StrictMode>,
);
